import React, { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import UserContext from 'context/user-context'
import SortableHeader from './cells/SortableHeader'
import FilterableHeader from 'shared/column-filters/FilterableHeader'
import AccountFilterableHeader from 'shared/column-filters/AccountFilterableHeader'
import VendorNameFilterableHeader from 'shared/column-filters/VendorNameFilterableHeader'
import AnalyticFilterableHeader from 'shared/column-filters/AnalyticFilterableHeader'
import TransactionListFilter from 'transactions/table/filters/transaction-list'
import { makeGetColumnOptions } from 'shared/column-filters/getColumnOptions'
import {
  customColumnFilterColumn,
  customColumnFilterOptions,
  filterableCustomColumnTypes
} from 'shared/column-filters/custom-column-filter'
import { getColumnDisplayName } from 'dashboard/utils'
import { useActiveAccountGrouping } from 'api/hooks/use-account-groupings'

function TableHeader({
  tableFilters,
  setTableFilters,
  columns,
  includeOrdering = true,
  tableColumns,
  hasSectionStatusColumn = false,
  dashboardSetParams = null,
  analyticsColumns = [],
  sectionId,
  customColumns = [],
  unifiedColumnOrder = null,
  dashboardId = null,
  canEdit = true,
  referenceSearch = null,
  onReferenceSearchHandled = null,
  reportFeatures = []
}) {
  const { municipality, municipality_sections } = useContext(UserContext)
  const [activePopoverId, setActivePopoverId] = useState(null)
  const [localReferenceSearch, setLocalReferenceSearch] = useState(null)

  // Auto-open the reference filter popover when a reference search arrives
  useEffect(() => {
    if (referenceSearch && tableColumns) {
      // Find the matching table column by accessor_key or database_column_name
      const matchingColumn = tableColumns.find(
        (tc) =>
          tc.accessor_key === referenceSearch.accessor_key ||
          tc.database_column_name === referenceSearch.database_column_name
      )
      if (matchingColumn) {
        // The popoverId for table columns is the database_column_name
        const columnEntry = columns.find(
          (c) => c === matchingColumn.database_column_name
        )
        if (columnEntry) {
          setLocalReferenceSearch(referenceSearch)
          setActivePopoverId(columnEntry)
        }
      }
      if (onReferenceSearchHandled) {
        onReferenceSearchHandled()
      }
    }
  }, [referenceSearch]) // eslint-disable-line react-hooks/exhaustive-deps

  // Clear local reference search when popover closes
  useEffect(() => {
    if (!activePopoverId && localReferenceSearch) {
      setLocalReferenceSearch(null)
    }
  }, [activePopoverId]) // eslint-disable-line react-hooks/exhaustive-deps

  const groupingName = useActiveAccountGrouping(dashboardId)?.name || null

  const sectionName = municipality_sections?.find(
    (ms) => ms.section_id === sectionId
  )?.section?.name

  // Delegates to the shared resolver rather than keeping its own copy of the segment
  // switch. Three near-identical copies had drifted, and only the shared one knew
  // about the account-group rollup columns — so this header showed "Fund" for seg_one
  // while showing "Segment One Group" for seg_one_group.
  // The rollup columns name the taxonomy they bucket by — "OCAS: Org Rollup". The View's own
  // grouping when the grid is inside one, else the district default.
  const columnTitle = (tableColumn) =>
    getColumnDisplayName(
      { ...tableColumn, name: tableColumn.display_name },
      municipality,
      groupingName
    )

  // Resolve filter options from the table_columns API response.
  const getOptionsForAccessorKey = makeGetColumnOptions(tableColumns, municipality)

  const renderRegularColumn = (columnName) => {
    if (columnName.startsWith('feature_review:')) {
      const featureKey = columnName.replace('feature_review:', '')
      const feature = reportFeatures.find(f => f.key === featureKey)
      const label = `${municipality?.abbreviation || ''}-${featureKey}`
      return (
        <th key={columnName} className="text-center">
          {feature?.description ? (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{feature.description}</Tooltip>}
            >
              <span style={{ cursor: 'help' }}>{label}</span>
            </OverlayTrigger>
          ) : (
            label
          )}
        </th>
      )
    } else if (columnName === 'cleared') {
      return <th key={columnName}></th>
    } else if (['view', 'processing_modal'].includes(columnName)) {
      return <th key={columnName}>ID</th>
    } else if (columnName === 'report_analytics_count') {
      return (
        <SortableHeader
          key={columnName}
          title="Flags"
          filters={tableFilters}
          setFilters={setTableFilters}
          column="report_analytics_count"
          includeOrdering={includeOrdering}
          popoverId="flags-sort"
          activePopoverId={activePopoverId}
          setActivePopoverId={setActivePopoverId}
        />
      )
    } else if (columnName === 'vendor') {
      return (
        <SortableHeader
          key={columnName}
          title="Vendor"
          filters={tableFilters}
          setFilters={setTableFilters}
          column="vendors.name"
          includeOrdering={includeOrdering}
          popoverId="vendor-sort"
          activePopoverId={activePopoverId}
          setActivePopoverId={setActivePopoverId}
        />
      )
    } else if (columnName === 'employee') {
      return <th key={columnName}>Employee </th>
    } else if (columnName === 'single_status') {
      return (
        <th
          key={columnName}
          style={{ cursor: includeOrdering ? 'pointer' : 'default' }}
          className="text-center"
        >
          Overall
        </th>
      )
    } else if (columnName === 'testing_status') {
      return (
        <th
          key={columnName}
          style={{
            cursor: includeOrdering ? 'pointer' : 'default'
          }}
          className="text-center"
        >
          Attributes
        </th>
      )
    } else if (columnName === 'status' && !hasSectionStatusColumn) {
      return (
        <th
          key={columnName}
          style={{
            cursor: includeOrdering ? 'pointer' : 'default'
          }}
          className="text-center"
        >
          Transaction Status
        </th>
      )
    } else if (columnName === 'employee_full_name') {
      return <th key={columnName}>Employee </th>
    } else if (columnName === 'line_amount') {
      return <th key={columnName}>Line Amount </th>
    } else if (columnName === 'vendor_id') {
      return <th key={columnName}>Vendor ID </th>
    } else if (columnName === 'add_to_list') {
      return (
        <th key={columnName} style={{ maxWidth: '75px', width: '75px' }}>
          <TransactionListFilter params={tableFilters} setParams={setTableFilters} />
        </th>
      )
    } else {
      const col = tableColumns?.find(
        (tc) => tc.database_column_name === columnName
      )

      // For all 'transaction_on' instances except on the Vendor view, show as 'Transaction On'.
      // For Vendor view, derive name from table column/system table column.
      if (col?.section_abbreviation !== 'Vendor' && columnName === 'transaction_on') {
        return <th key={columnName}>Transaction On </th>
      }
      const title = col && columnTitle(col)
      const sortColumn =
        col &&
        col.is_sortable &&
        `${col.section_abbreviation.toLowerCase()}_transaction_details.${columnName}`
      const filterTypes = [
        'basic',
        'boolean',
        'date',
        'min_max',
        'min_max_percent',
        'multiselect',
        'mcc_select',
        'group_multiselect',
        'select',
        'greater_than',
        'reference',
        'time_in_seconds'
      ]
      // Exclude basic filters for GL account columns in PCard - account filters above table should apply instead
      const glColumns = [
        'card_transaction_gl_department',
        'card_transaction_gl_object',
        'card_transaction_gl_org',
        'card_transaction_gl_project'
      ]
      const isGLColumn = glColumns.includes(columnName)
      const hasFilter =
        col && filterTypes.includes(col.filter_type) && !isGLColumn

      // Only a reference vendor_name filters through the vendor association; a plain
      // detail column (Contracts) takes the standard text filter instead.
      const isVendorNameColumn =
        col && col.database_column_name === 'vendor_name' && col.filter_type === 'reference'

      // Check if this is an account column that should use AccountFilterableHeader
      const accountColumns = [
        'org',
        'object',
        'project',
        'seg_one',
        'seg_two',
        'seg_three',
        'seg_four',
        'seg_five',
        'seg_six',
        'seg_seven',
        'seg_eight'
      ]
      const isAccountColumn = accountColumns.includes(columnName)

      const hasAllocations = municipality_sections.find(
        (ms) => ms.section_id === sectionId
      )?.has_allocations

      if (isVendorNameColumn) {
        return (
          <VendorNameFilterableHeader
            key={columnName}
            column={col}
            title={title}
            params={tableFilters}
            setParams={setTableFilters}
            includeOrdering={includeOrdering}
            sortColumn={sortColumn}
            dashboardSetParams={dashboardSetParams}
            popoverId={columnName}
            activePopoverId={activePopoverId}
            setActivePopoverId={setActivePopoverId}
            canEdit={canEdit}
            initialSearch={
              localReferenceSearch?.database_column_name === 'vendor_name' ||
              localReferenceSearch?.accessor_key === col?.accessor_key
                ? localReferenceSearch?.search_term || ''
                : ''
            }
          />
        )
      } else if (
        isAccountColumn &&
        (sectionName === 'PCard' ? hasAllocations : true)
      ) {
        return (
          <AccountFilterableHeader
            key={columnName}
            accountType={columnName}
            title={title}
            params={tableFilters}
            setParams={setTableFilters}
            includeOrdering={includeOrdering}
            dashboardSetParams={dashboardSetParams}
            popoverId={columnName}
            activePopoverId={activePopoverId}
            setActivePopoverId={setActivePopoverId}
            canEdit={canEdit}
          />
        )
      } else if (hasFilter) {
        return (
          <FilterableHeader
            key={columnName}
            column={col}
            title={title}
            params={tableFilters}
            setParams={setTableFilters}
            includeOrdering={includeOrdering}
            sortColumn={sortColumn}
            getOptions={getOptionsForAccessorKey}
            dashboardSetParams={dashboardSetParams}
            dashboardId={dashboardId}
            popoverId={columnName}
            activePopoverId={activePopoverId}
            setActivePopoverId={setActivePopoverId}
            canEdit={canEdit}
            referenceSearch={
              col?.filter_type === 'reference' && localReferenceSearch?.accessor_key === col?.accessor_key
                ? localReferenceSearch
                : null
            }
          />
        )
      } else if (col && col.is_sortable) {
        return (
          <SortableHeader
            key={columnName}
            title={title}
            filters={tableFilters}
            setFilters={setTableFilters}
            column={sortColumn}
            includeOrdering={includeOrdering}
            popoverId={`${columnName}-sort`}
            activePopoverId={activePopoverId}
            setActivePopoverId={setActivePopoverId}
          />
        )
      } else {
        return <th key={columnName}>{title || columnName}</th>
      }
    }
  }

  const renderColumn = (columnData, type) => {
    if (type === 'custom') {
      // Custom columns use their database column name for sorting
      const sortColumn = `custom_column_${columnData.id}`
      const mockColumn = customColumnFilterColumn(columnData)

      // Use FilterableHeader for most custom column types to enable filtering
      if (filterableCustomColumnTypes.includes(columnData.column_type)) {
        return (
          <FilterableHeader
            key={`custom-${columnData.id}`}
            column={mockColumn}
            title={columnData.name}
            params={tableFilters}
            setParams={setTableFilters}
            includeOrdering={includeOrdering}
            sortColumn={sortColumn}
            getOptions={() => customColumnFilterOptions(columnData)}
            dashboardSetParams={dashboardSetParams}
            popoverId={`custom-${columnData.id}`}
            activePopoverId={activePopoverId}
            setActivePopoverId={setActivePopoverId}
            canEdit={canEdit}
          />
        )
      } else {
        // Fallback to SortableHeader for unsupported types
        return (
          <SortableHeader
            key={`custom-${columnData.id}`}
            title={columnData.name}
            filters={tableFilters}
            setFilters={setTableFilters}
            column={sortColumn}
            includeOrdering={includeOrdering}
            popoverId={`custom-${columnData.id}-sort`}
            activePopoverId={activePopoverId}
            setActivePopoverId={setActivePopoverId}
          />
        )
      }
    } else if (type === 'analytics') {
      // Render analytics column header

      return (
        dashboardId ? (
          <AnalyticFilterableHeader
            key={`analytics-${columnData.id}`}
            analytic={columnData}
            dashboardId={dashboardId}
            params={tableFilters}
            setParams={dashboardSetParams || setTableFilters}
            popoverId={`analytics-${columnData.id}`}
            activePopoverId={activePopoverId}
            setActivePopoverId={setActivePopoverId}
            canEdit={canEdit}
          />
        ) : (
          <th key={`analytics-${columnData.id}`} className="text-center">
            {columnData.name.length > 50
              ? columnData.name.substring(0, 47) + '...'
              : columnData.name}
          </th>
        )
      )
    } else {
      return renderRegularColumn(columnData)
    }
  }

  return (
    <thead>
      <tr>
        {/* Use unified column order if available, otherwise fallback to separate rendering */}
        {unifiedColumnOrder && unifiedColumnOrder.length > 0 ? (
          unifiedColumnOrder.map((item) => renderColumn(item.data, item.type))
        ) : (
          <>
            {columns.map((columnName) => renderRegularColumn(columnName))}
            {customColumns.map((customColumn) => (
              <th key={`custom-${customColumn.id}`} className="text-center">
                {customColumn.name}
              </th>
            ))}
            {/* Analytics columns are rendered at the end when no unified order is available */}
            {analyticsColumns.map((analyticsColumn) =>
              dashboardId ? (
                <AnalyticFilterableHeader
                  key={`analytics-${analyticsColumn.id}`}
                  analytic={analyticsColumn}
                  dashboardId={dashboardId}
                  params={tableFilters}
                  setParams={dashboardSetParams || setTableFilters}
                  popoverId={`analytics-${analyticsColumn.id}`}
                  activePopoverId={activePopoverId}
                  setActivePopoverId={setActivePopoverId}
                  canEdit={canEdit}
                />
              ) : (
                <th key={`analytics-${analyticsColumn.id}`} className="text-center">
                  {analyticsColumn.name.length > 50
                    ? analyticsColumn.name.substring(0, 47) + '...'
                    : analyticsColumn.name}
                </th>
              )
            )}
          </>
        )}
      </tr>
    </thead>
  )
}

TableHeader.propTypes = {
  tableFilters: PropTypes.shape({
    orderBy: PropTypes.string,
    orderDirection: PropTypes.string
  }),
  setTableFilters: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired
}

export default TableHeader
